import type {
  EnhancedDeal,
  ProjectOnboarding,
  ProjectPhase,
  ProjectTask,
  TaskGroup,
  TimberProject,
} from '../types';
import { compareProjectsByWorkQueueThenNumber } from './archEngineerWorkQueue';
import {
  onboardingItemToProjectTask,
} from './onboardingTaskBridge';
import { taskInvolvesUser } from './projectTaskUsers';

export interface ArchitectStandupProjectGroup {
  project: TimberProject;
  deal: EnhancedDeal | undefined;
  openTaskCount: number;
}

export function buildArchitectActiveProjectGroups(args: {
  timberProjects: TimberProject[];
  enhancedDeals: EnhancedDeal[];
  projectTasks: ProjectTask[];
  taskGroups: TaskGroup[];
  projectOnboardings: ProjectOnboarding[];
  userId: string;
  queuesByUserId: string[];
  dealsById: Map<string, EnhancedDeal>;
}): ArchitectStandupProjectGroup[] {
  const groups: ArchitectStandupProjectGroup[] = [];

  for (const project of args.timberProjects) {
    if (!project.id) continue;
    if (project.status === 'completed') continue;
    const phase = project.phase || 'contracted';
    if (phase !== 'estimating' && phase !== 'preconstruction' && phase !== 'contracted') continue;

    const deal = project.dealId ? args.dealsById.get(project.dealId) : undefined;
    const pid = project.id;
    const groupsForProject = args.taskGroups.filter(g => g.projectId === pid);
    const onboardingDoc = args.projectOnboardings.find(ob => ob.projectId === pid);
    const onboardingMirrored: ProjectTask[] = onboardingDoc?.items?.length
      ? onboardingDoc.items.map(it => onboardingItemToProjectTask(it, pid, groupsForProject))
      : [];
    const firestoreForProject = args.projectTasks.filter(t => t.projectId === pid);
    const mergedForProject = [...onboardingMirrored, ...firestoreForProject];

    const openTasks = mergedForProject.filter(
      t => taskInvolvesUser(t, args.userId) && t.status !== 'completed',
    );
    if (openTasks.length === 0) continue;

    groups.push({
      project,
      deal,
      openTaskCount: openTasks.length,
    });
  }

  groups.sort((a, b) =>
    compareProjectsByWorkQueueThenNumber(
      a.project,
      b.project,
      args.queuesByUserId,
      args.dealsById,
    ),
  );

  return groups;
}

export function phaseLabel(phase: ProjectPhase | undefined): string {
  switch (phase) {
    case 'estimating':
      return 'Estimating';
    case 'preconstruction':
      return 'Precon';
    default:
      return 'Under Contract';
  }
}
